import store from '@ps/store'
import { getClipboard } from '@ps/workbench/copy-paste'
import {
  animationCanBePasted,
  copyAnimation,
  pasteAnimation,
  rewriteAnimation,
} from '@ps/workbench/copy-paste/animation'
import {
  copyEvent,
  eventCanBePasted,
  pasteEvent,
  pasteEventByCopyNode,
} from '@ps/workbench/copy-paste/event'
import { pasteStyle, styleCanBePasted } from '@ps/workbench/copy-paste/style'
import { copyNode } from '@ps/workbench/copy-paste/node'
import { Notification } from 'element-ui'
import _ from 'lodash'
import { pasteLayout, layoutCanBePasted } from '@ps/workbench/copy-paste/layout'
import i18n from '@ps/lang'
import { sortNodeByReverseTree } from '@ps/views/workbench/layer-manager/layer'
import {
  filterRelatedNode,
  getConnectDrawLineInScene,
  hasEraseNodeInScene,
  hasMaskComponentNodeInScene,
  isDrawLineAreaNode,
  isDrawLineNode,
  isEraseNode,
  isMaskComponentNode,
  isCarouselNode,
  isCarouselNodeChild,
  hasCarouselNodeInScene,
  isQuickLayout,
} from '@ps/bridge/qc/base'
import { isAudio, isVideo, isScene, isEmptyNode } from '@ps/bridge/qc/base.ts'
import { SceneType } from '@ps/bridge/qc/scripts/scene-config'
import { Message } from 'element-ui'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import { hasDrawLineInScene } from '@ps/bridge/qc/base'

const message = {
  copy: i18n.t('workbench.psNavbar.navbarMiddle.copy.record'),
  paste: i18n.t('workbench.psNavbar.navbarMiddle.paste.success'),
}

function notify(msg) {
  Notification({
    message: msg,
    type: 'success',
    position: 'bottom-right',
  })
}

function pastePermision(nodes, currentSceneType) {
  let pool = []
  pool = pool.concat(nodes)
  nodes.forEach((node) => {
    // 遍历节点池
    pool = pool.concat(getDescendants(node, false, true))
  })
  const forbidden = {
    [SceneType.GLOBAL]: {
      rule: (node) => {
        return isQuickLayout(node)
      },
    },
    [SceneType.PLAY]: {
      rule: (node) => {
        // 禁止复制拖拽节点，音频
        return isAudio(node) || isEmptyNode(node)
      },
      ruleTips: '',
    },
    [SceneType.NORMAL]: {
      rule: (node) => {
        return isQuickLayout(node)
      },
    },
  }
  return pool.some((node) => forbidden[currentSceneType].rule(node))
}

export default {
  namespaced: true,

  state: {
    clipboard: {
      nodes: null,
      originalNode: null,
      event: null,
      animation: null,
      style: null,
      responseEvent: null,
      positionAndSize: null,
    },
    hasCopyNode: false,
    hasStyle: false,
    hasAnimation: false,
    hasEvent: false,
    hasPositionAndSize: false,
  },

  getters: {
    canPaste(state) {
      return (
        state.hasCopyNode ||
        state.hasStyle ||
        state.hasAnimation ||
        state.hasEvent
      )
    },

    canPasteLayer(state) {
      const sceneNode = store.state.workspace.sceneManager.currentSceneNode
      const copyNodes = state.clipboard.nodes || []
      const getSelfAndChild = (list) => {
        const temp = list.reduce(
          (total, cur) => [...total, ...getDescendants(cur)],
          [],
        )
        return [...temp, ...list]
      }
      // 1个场景内仅支持添加1个连线组件
      if (
        state.clipboard.nodes &&
        getSelfAndChild(copyNodes).some((item) => isDrawLineNode(item)) &&
        hasDrawLineInScene(sceneNode)
      ) {
        return false
      }
      // 1个场景内仅支持添加1个擦除组件
      if (
        state.clipboard.nodes &&
        getSelfAndChild(copyNodes).some((item) => isEraseNode(item)) &&
        hasEraseNodeInScene(sceneNode)
      ) {
        return false
      }
      // 1个场景内仅支持添加1个遮罩组件
      if (
        state.clipboard.nodes &&
        getSelfAndChild(copyNodes).some((item) => isMaskComponentNode(item)) &&
        hasMaskComponentNodeInScene(sceneNode)
      ) {
        return false
      }
      // 1个场景内仅支持添加1个轮播组件
      if (
        state.clipboard.nodes &&
        getSelfAndChild(copyNodes).some((item) => isCarouselNode(item)) &&
        hasCarouselNodeInScene(sceneNode)
      ) {
        return false
      }
      // 轮播组子节点不支持粘贴
      if (
        state.clipboard.nodes &&
        state.clipboard.nodes.some((item) => isCarouselNodeChild(item))
      ) {
        return false
      }
      return state.hasCopyNode
    },

    canPasteStyle(state) {
      return (
        state.hasStyle &&
        styleCanBePasted(
          store.state.workspace.game.activeNodeList,
          state.clipboard.originalNode,
        )
      )
    },

    canPastePositionAndSize(state) {
      return (
        state.hasPositionAndSize &&
        layoutCanBePasted(
          store.state.workspace.game.activeNodeList,
          state.clipboard.originalNode,
        )
      )
    },

    canPasteAnimation(state) {
      return (
        state.hasAnimation &&
        animationCanBePasted(
          store.state.workspace.game.activeNodeList,
          state.originalNode,
        )
      )
    },

    canPasteEvent(state) {
      return (
        state.hasEvent &&
        eventCanBePasted(
          store.state.workspace.game.activeNodeList,
          state.clipboard.event,
        )
      )
    },
  },

  mutations: {
    updateClipboard(state) {
      state.clipboard = getClipboard()
      state.hasCopyNode = !_.isEmpty(state.clipboard.nodes)
      state.hasStyle = !_.isEmpty(state.clipboard.style)
      state.hasPositionAndSize = !_.isEmpty(state.clipboard.positionAndSize)
      state.hasAnimation = !_.isEmpty(state.clipboard.animation)
      state.hasEvent = !_.isEmpty(state.clipboard.event)
    },
  },

  actions: {
    copyNode({ commit }, { nodes }) {
      copyNode(nodes)
      commit('updateClipboard')
      notify(message.copy)
    },

    pasteNode({ commit, state, rootState }, { targetNode, index } = {}) {
      // 如果没有 copyNode 节点，则直接返回
      if (_.isEmpty(state.clipboard.nodes)) return

      if (
        (targetNode && !_.isNumber(index)) ||
        (!targetNode && _.isNumber(index))
      ) {
        throw new Error('targetNode or index must be provided')
      }

      const nodes = state.clipboard.nodes
      if (
        pastePermision(
          nodes,
          rootState.workspace.game.sceneManager.sceneEditType,
        )
      ) {
        Message({
          message: this.$t('workbench.sceneSetting.playScene.errorMsg'),
          type: 'error',
          duration: 3 * 1000,
        })
        return
      }
      const activeNodeList = store.state.workspace.game.activeNodeList
      const currentSceneNode =
        store.state.workspace.sceneManager.currentSceneNode
      const hasActiveNodeList = activeNodeList && activeNodeList.length > 0

      if (!targetNode) {
        // 有选中节点时，将节点插入到最上面的选中节点的后面
        if (hasActiveNodeList) {
          const sortNodes = sortNodeByReverseTree(
            activeNodeList,
            currentSceneNode.children || [],
          )
          const firstChild = sortNodes[0]
          targetNode = firstChild.parent
          index = targetNode.getChildIndex(firstChild)
        } else {
          // 粘贴到最后面，但是由于图层面板跟真实的节点树是相反的，所以粘贴后节点在图层面板展示在最上面
          targetNode = currentSceneNode
          index = currentSceneNode.children.length
        }
      }

      // 复制内容中包含视频、音频，复制目标只能在场景节点
      if (
        !isScene(targetNode) &&
        nodes.some((node) => isVideo(node) || isAudio(node))
      ) {
        targetNode = currentSceneNode
        index = currentSceneNode.children.length
      }
      // 视频节点放在最底下
      if (nodes.some((node) => isVideo(node))) {
        const videos = currentSceneNode.children.filter((child) =>
          isVideo(child),
        )
        index = videos.length
      }
      // 起止区域节点复制到连线组件里的子节点的最后
      if (nodes.some((node) => isDrawLineAreaNode(node))) {
        targetNode = getConnectDrawLineInScene(currentSceneNode)
        if (!targetNode) return
        index = targetNode.children.length
      }

      // 使用第一个节点作为目标节点
      store.dispatch('workspace/game/pasteNode', {
        // filterRelatedNode 过滤复制节点中的关联节点
        copyNodes: filterRelatedNode(nodes),
        targetNode,
        index,
      })
    },

    copyAnimation({ commit }, { node, groupId, uuid }) {
      copyAnimation({
        node,
        groupId,
        uuid,
      })
      commit('updateClipboard')
      notify(message.copy)
    },

    pasteAnimation({ state }, { targetNodes, record = true }) {
      const animationScripts = pasteAnimation({
        targetNodes,
        animations: state.clipboard.animation,
        record,
        currentNode: store.state.workspace.game.activeNode,
      })
      notify(message.paste)
      return animationScripts
    },

    rewriteAnimation({ state }, { targetNodes, animations, record = true }) {
      const animationScripts = rewriteAnimation({
        targetNodes,
        animations,
        record,
        currentNode: store.state.workspace.game.activeNode,
      })
      notify(message.paste)
      return animationScripts
    },

    pasteStyle({ state }, { targetNodes, record = true }) {
      pasteStyle({
        targetNodes,
        record,
        style: state.clipboard.style,
      })
      notify(message.paste)
    },

    copyEvent({ commit }, { id, node, isAll }) {
      copyEvent({
        id,
        node,
        isAll,
      })
      commit('updateClipboard')
      notify(message.copy)
    },

    pasteEvent({ state }, { targetNodes, record = true }) {
      pasteEvent({
        targetNodes,
        event: state.clipboard.event,
        originalNode: state.clipboard.originalNode,
        record,
      })
      notify(message.paste)
    },

    pasteEventByCopyNode(
      { state },
      { targetNodes, originalNodes, record = true },
    ) {
      targetNodes.forEach((node, index) => {
        pasteEventByCopyNode({
          targetNodes: [node],
          originalNode: originalNodes
            ? originalNodes[index]
            : state.clipboard.nodes[index],
          record,
        })
      })
      notify(message.paste)
    },

    pastePositionAndSize({ state }, { targetNodes, record = true }) {
      pasteLayout({
        targetNodes,
        record,
        style: state.clipboard.positionAndSize,
        originalNode: state.clipboard.originalNode,
      })
      notify(message.paste)
    },
  },
}
